import React from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Check, ArrowRight } from 'lucide-react';
import { pricingPlans } from '../data/mock';

const Pricing = () => {
  const handleRequestQuote = (planName) => {
    const phone = "+919217401431";
    const message = `Hi, I'm interested in the ${planName}. Please share more details.`;
    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-cyan-50">
      {/* Header Section */}
      <section className="bg-gradient-to-r from-cyan-600 to-teal-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-5xl font-bold mb-6">Simple, Transparent Pricing</h1>
          <p className="text-xl text-cyan-50 max-w-3xl mx-auto">
            Choose the plan that fits your needs. All plans include IoT monitoring, mobile app access, and expert support.
          </p>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-20 -mt-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-8">
            {pricingPlans.map((plan, index) => (
              <Card 
                key={plan.id}
                className={`relative overflow-hidden border-2 transition-all duration-300 flex flex-col ${
                  plan.popular 
                    ? 'border-cyan-500 shadow-2xl scale-105 lg:scale-110' 
                    : 'border-gray-200 hover:border-cyan-300 shadow-lg'
                }`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {plan.popular && (
                  <div className="absolute top-0 right-0 left-0">
                    <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white text-center py-2 text-sm font-semibold">
                      MOST POPULAR
                    </div>
                  </div>
                )}

                <CardHeader className={plan.popular ? 'pt-14' : 'pt-6'}>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription className="text-base">
                    {plan.description}
                  </CardDescription>
                  
                  <div className="pt-6">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-gray-900">
                        {plan.price.split('/')[0]}
                      </span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">{plan.period}</p>
                  </div>
                </CardHeader>

                <CardContent className="flex-grow">
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-3">
                        <Check className="h-5 w-5 text-teal-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>

                <CardFooter className="pt-6 border-t">
                  <Button 
                    onClick={() => handleRequestQuote(plan.name)}
                    className={`w-full ${
                      plan.popular
                        ? 'bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white'
                        : 'bg-white border-2 border-cyan-500 text-cyan-600 hover:bg-cyan-50'
                    }`}
                  >
                    Request Quote
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-16 bg-white rounded-2xl shadow-lg p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">
              What's Included in All Plans
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">IoT Monitoring</h3>
                <p className="text-sm text-gray-600">
                  Real-time TDS, flow, and filter health tracking
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Mobile App</h3>
                <p className="text-sm text-gray-600">
                  iOS and Android apps for remote monitoring
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-100 to-teal-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Check className="h-8 w-8 text-cyan-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-2">Expert Support</h3>
                <p className="text-sm text-gray-600">
                  Dedicated support team for all your queries
                </p>
              </div>
            </div>
          </div>

          {/* Custom Enterprise */}
          <div className="mt-12 bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl shadow-2xl p-12 text-white text-center">
            <h2 className="text-3xl font-bold mb-4">
              Need a Custom Solution?
            </h2>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl mx-auto">
              For large enterprises, multiple locations, or special requirements, we offer customized plans with dedicated support and flexible terms.
            </p>
            <Button 
              onClick={() => window.location.href = '/contact'}
              size="lg" 
              className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
            >
              Contact Sales Team
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What's included in the annual service?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Annual service includes scheduled maintenance visits, filter health checks, system diagnostics, cleaning, and emergency support. The number of visits varies by plan.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Are filter replacements included?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Filter replacements are included in the Advanced Home Plan for the first year. For other plans, filters are available at discounted rates with free installation during maintenance visits.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Can I upgrade my plan later?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  Yes! You can upgrade your plan at any time. Contact our support team, and we'll help you transition smoothly with prorated pricing.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">What if I move to a new location?</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  We offer free relocation service within the same city. For intercity moves, we can arrange installation at your new location at a nominal charge.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;
